/* Deep links into the QC form: /qcform/:substoryId.
 *
 * The normal way into the form is QcGrid pushing the whole row (plus the day's program
 * list) through react-router navigation state. A deep link carries none of that -- only
 * an id -- so the page has to refetch the row and seed the grid window that the timeline
 * and video fetches read out of SharedStateContext. Those context values are persisted in
 * localStorage from whatever the operator last looked at, which for a link to an archive
 * row on another channel or another day is simply the wrong window. */

export const qcFormPath = (substoryId) => `/qcform/${encodeURIComponent(substoryId)}`;

// A substory id is a bare positive integer; anything else in the URL is a typo or a
// hand-edited link, and must not reach the API as a path segment.
export const parseSubstoryIdParam = (value) => {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (!/^\d+$/.test(text)) return null;
    const id = Number(text);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const DEEP_LINK_WINDOW_PADDING_MINUTES = 45;

/* The timeline window to show around a deep-linked substory, as the three separate
 * context values the form's fetches expect: a day (`date`) and two times (`time`,
 * `endtime`) that are formatted against that day. Because the two ends are formatted
 * against one date, the window is clamped to that day -- a substory running past
 * midnight shows a timeline that stops at 23:59:59 rather than one that ends before it
 * begins. */
// `new Date(null)` is the epoch, not an invalid date, so an absent timestamp has to be
// rejected before the Date is built or a null start_time silently becomes 1970.
const timestamp = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
};

export const deepLinkTimelineWindow = (row, { paddingMinutes = DEEP_LINK_WINDOW_PADDING_MINUTES } = {}) => {
    const start = timestamp(row?.start_time);
    if (!start) return null;

    const end = timestamp(row?.end_time);
    const substoryEnd = end && end.getTime() > start.getTime() ? end : start;

    const dayStart = new Date(start);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(start);
    dayEnd.setHours(23, 59, 59, 0);

    const padding = Math.max(0, paddingMinutes) * 60000;
    return {
        date: dayStart,
        start: new Date(Math.max(dayStart.getTime(), start.getTime() - padding)),
        end: new Date(Math.min(dayEnd.getTime(), substoryEnd.getTime() + padding)),
    };
};

/* True while the browser is on a deep-linked QC form.
 *
 * The form seeds SharedStateContext with the window around its one substory so the
 * timeline and video fetches have a channel and a day to ask for. That window is not a
 * filter the operator chose -- it comes out of the row -- so the QC grid's persisted
 * filters must not be overwritten with it. */
export const isDeepLinkPath = (pathname) => /^\/qcform\/\d+\/?$/.test(String(pathname || ""));
